import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { MapPin, Building2, AlertTriangle, Loader2, RefreshCw, Layers, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

// Leaflet imports
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom marker icons based on violation count
const createCustomIcon = (violationCount) => {
  const color = violationCount >= 10 ? '#ef4444' : violationCount >= 5 ? '#f59e0b' : '#22c55e';
  
  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div style="
        background: ${color};
        width: 32px;
        height: 32px;
        border-radius: 50% 50% 50% 0;
        transform: rotate(-45deg);
        border: 3px solid white;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
      ">
        <span style="
          transform: rotate(45deg);
          color: white;
          font-size: 12px;
          font-weight: bold;
        ">${violationCount}</span>
      </div>
    `,
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32],
  });
};

// Component to fit map bounds to markers
function FitBounds({ markers }) {
  const map = useMap();
  
  useEffect(() => {
    if (markers.length > 0) {
      const bounds = L.latLngBounds(markers.map(m => [m.lat, m.lng]));
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 });
    }
  }, [markers, map]);
  
  return null;
}

// Dataset labels
const DATASET_LABELS = {
  dob_violations: 'DOB',
  fire_violations: 'Fire',
  ecb_violations: 'ECB',
  permits: 'Permits',
  dot_violations: 'DOT',
  dob_now: 'DOB NOW',
  bsa_applications: 'BSA'
};

export default function MapPage() {
  const { user, api } = useAuth();
  const [watchlists, setWatchlists] = useState([]);
  const [markers, setMarkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [geocoding, setGeocoding] = useState(false);
  const [selectedMarker, setSelectedMarker] = useState(null);

  // NYC center coordinates
  const nycCenter = [40.7128, -74.0060];

  // Fetch watchlists
  const fetchWatchlists = useCallback(async () => {
    try {
      const res = await api().get('/watchlist');
      setWatchlists(res.data);
      return res.data;
    } catch (err) {
      console.error('Failed to fetch watchlists:', err);
      return [];
    }
  }, [api]);

  // Geocode an address using NYC's GeoSearch API
  const geocodeAddress = async (address, borough) => {
    try {
      const query = borough ? `${address}, ${borough}, NY` : `${address}, New York, NY`;
      const res = await api().get(`/address/search?query=${encodeURIComponent(query)}`);
      
      if (res.data.suggestions?.length > 0) {
        const suggestion = res.data.suggestions[0];
        // The Zola API returns coordinates
        if (suggestion.lat && suggestion.lng) {
          return { lat: suggestion.lat, lng: suggestion.lng };
        }
      }
      
      // Fallback: Use NYC Planning's geoclient or estimate based on borough
      return null;
    } catch (err) {
      console.error('Geocoding failed:', err);
      return null;
    }
  };

  // Fetch violation counts for a property
  const fetchViolationCount = async (property) => {
    try {
      const datasets = property.datasets || user?.datasets_access || ['dob_violations'];
      let totalCount = 0;
      const violationsByType = {};
      
      for (const dataset of datasets.slice(0, 3)) { // Limit to 3 datasets for speed
        if (!user?.datasets_access?.includes(dataset)) continue;
        
        const params = new URLSearchParams({ dataset, limit: '50' });
        if (property.address) params.append('address', property.address);
        if (property.block) params.append('block', property.block);
        if (property.lot) params.append('lot', property.lot);
        
        const res = await api().get(`/violations/search?${params}`);
        const count = res.data.data?.length || 0;
        totalCount += count;
        violationsByType[dataset] = count;
      }
      
      return { total: totalCount, byType: violationsByType };
    } catch (err) {
      return { total: 0, byType: {} };
    }
  };

  // Fetch quick risk score for a property
  const fetchQuickRiskScore = async (property) => {
    try {
      if (!property.block || !property.lot) return null;
      
      const params = new URLSearchParams({
        block: property.block,
        lot: property.lot
      });
      if (property.borough) params.append('borough', property.borough);
      
      const res = await api().get(`/property/risk-score/quick?${params}`);
      return res.data;
    } catch (err) {
      console.error('Failed to fetch risk score:', err);
      return null;
    }
  };

  // Load and geocode all watchlist properties
  const loadMapData = async () => {
    setLoading(true);
    setGeocoding(true);
    
    try {
      const watchlistData = await fetchWatchlists();
      const newMarkers = [];
      
      for (const watchlist of watchlistData) {
        for (const property of watchlist.properties || []) {
          // Try to geocode the address
          let coords = await geocodeAddress(property.address, property.borough);
          
          // If geocoding fails, use approximate borough centers
          if (!coords) {
            coords = getBoroughCenter(property.borough || 'Manhattan');
          }
          
          if (coords) {
            // Fetch violation count
            const violations = await fetchViolationCount(property);
            
            // Fetch risk score
            const riskScore = await fetchQuickRiskScore(property);
            
            newMarkers.push({
              id: `${watchlist.id}-${property.address}-${property.block}`,
              lat: coords.lat,
              lng: coords.lng,
              address: property.address || `Block ${property.block}, Lot ${property.lot}`,
              block: property.block,
              lot: property.lot,
              borough: property.borough,
              watchlistName: watchlist.name,
              datasets: property.datasets || [],
              violationCount: violations.total,
              violationsByType: violations.byType,
              riskScore: riskScore?.score || 0,
              riskLevel: riskScore?.level || 'unknown'
            });
          }
        }
      }
      
      setMarkers(newMarkers);
      
      if (newMarkers.length === 0 && watchlistData.length > 0) {
        toast.info('Add properties to your watchlist to see them on the map');
      }
    } catch (err) {
      console.error('Failed to load map data:', err);
      toast.error('Failed to load map data');
    } finally {
      setLoading(false);
      setGeocoding(false);
    }
  };

  // Get approximate borough center for fallback
  const getBoroughCenter = (borough) => {
    const centers = {
      'Manhattan': { lat: 40.7831, lng: -73.9712 },
      'Brooklyn': { lat: 40.6782, lng: -73.9442 },
      'Queens': { lat: 40.7282, lng: -73.7949 },
      'Bronx': { lat: 40.8448, lng: -73.8648 },
      'Staten Island': { lat: 40.5795, lng: -74.1502 }
    };
    return centers[borough] || centers['Manhattan'];
  };

  useEffect(() => {
    loadMapData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="map-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white">Property Map</h1>
            <p className="text-zinc-400">
              {markers.length > 0 
                ? `${markers.length} watchlist ${markers.length === 1 ? 'property' : 'properties'} on map`
                : 'View your watchlist properties on the map'}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              onClick={loadMapData} 
              disabled={loading}
              className="btn-secondary"
              data-testid="refresh-map-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Refresh
            </Button>
          </div>
        </div>

        {/* Legend - Prominent at top */}
        <div className="bg-gradient-to-r from-zinc-900 to-zinc-800 rounded-xl p-4 border border-white/10">
          <div className="flex flex-wrap items-center justify-center gap-8">
            <span className="text-sm font-semibold text-white">Pin Color Key:</span>
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 rounded-full bg-emerald-500 shadow-lg shadow-emerald-500/30"></div>
              <span className="text-sm text-zinc-200 font-medium">0-4 violations (Compliant)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 rounded-full bg-amber-500 shadow-lg shadow-amber-500/30"></div>
              <span className="text-sm text-zinc-200 font-medium">5-9 violations (Moderate)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 rounded-full bg-rose-500 shadow-lg shadow-rose-500/30"></div>
              <span className="text-sm text-zinc-200 font-medium">10+ violations (High Risk)</span>
            </div>
          </div>
        </div>

        {/* Map Container */}
        <div className="glass-card rounded-xl overflow-hidden" style={{ height: '600px' }}>
          {loading && markers.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full">
              <Loader2 className="w-8 h-8 text-indigo-400 animate-spin mb-4" />
              <p className="text-zinc-400">
                {geocoding ? 'Geocoding addresses...' : 'Loading map data...'}
              </p>
            </div>
          ) : watchlists.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full">
              <MapPin className="w-12 h-12 text-zinc-600 mb-4" />
              <h3 className="text-lg font-medium text-white mb-2">No Watchlists Yet</h3>
              <p className="text-zinc-400 mb-4">Create a watchlist to see properties on the map</p>
              <Button 
                onClick={() => window.location.href = '/watchlist'}
                className="btn-primary"
              >
                Go to Watchlist
              </Button>
            </div>
          ) : (
            <MapContainer
              center={nycCenter}
              zoom={11}
              style={{ height: '100%', width: '100%' }}
              className="map-dark-theme"
            >
              {/* Dark theme tile layer */}
              <TileLayer
                attribution='&copy; <a href="https://carto.com/">CARTO</a>'
                url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
              />
              
              {/* Fit bounds to markers */}
              {markers.length > 0 && <FitBounds markers={markers} />}
              
              {/* Property markers */}
              {markers.map((marker) => (
                <Marker
                  key={marker.id}
                  position={[marker.lat, marker.lng]}
                  icon={createCustomIcon(marker.violationCount)}
                  eventHandlers={{
                    click: () => setSelectedMarker(marker)
                  }}
                >
                  <Popup className="custom-popup">
                    <div className="p-1 min-w-[280px]">
                      <div className="flex items-start gap-2 mb-2">
                        <Building2 className="w-5 h-5 text-indigo-500 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900">{marker.address}</h3>
                          {marker.block && marker.lot && (
                            <p className="text-xs text-gray-500">Block {marker.block}, Lot {marker.lot}</p>
                          )}
                        </div>
                        {/* Risk Score Badge */}
                        {marker.riskScore > 0 && (
                          <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold ${
                            marker.riskScore >= 70 ? 'bg-rose-100 text-rose-700' :
                            marker.riskScore >= 50 ? 'bg-orange-100 text-orange-700' :
                            marker.riskScore >= 30 ? 'bg-amber-100 text-amber-700' :
                            'bg-emerald-100 text-emerald-700'
                          }`}>
                            <Shield className="w-3 h-3" />
                            {marker.riskScore}
                          </div>
                        )}
                      </div>
                      
                      {/* Risk Level Bar */}
                      {marker.riskScore > 0 && (
                        <div className="mb-3">
                          <div className="flex justify-between items-center mb-1">
                            <span className="text-[10px] font-medium text-gray-600 uppercase">Risk Score</span>
                            <span className={`text-[10px] font-bold uppercase ${
                              marker.riskLevel === 'critical' ? 'text-rose-600' :
                              marker.riskLevel === 'high' ? 'text-orange-600' :
                              marker.riskLevel === 'moderate' ? 'text-amber-600' :
                              'text-emerald-600'
                            }`}>
                              {marker.riskLevel}
                            </span>
                          </div>
                          <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className={`h-full rounded-full transition-all ${
                                marker.riskScore >= 70 ? 'bg-rose-500' :
                                marker.riskScore >= 50 ? 'bg-orange-500' :
                                marker.riskScore >= 30 ? 'bg-amber-500' :
                                'bg-emerald-500'
                              }`}
                              style={{ width: `${marker.riskScore}%` }}
                            />
                          </div>
                        </div>
                      )}
                      
                      <div className="text-xs text-gray-500 mb-2">
                        Watchlist: {marker.watchlistName}
                      </div>
                      
                      <div className={`flex items-center gap-2 p-2 rounded ${
                        marker.violationCount >= 10 ? 'bg-rose-50' : 
                        marker.violationCount >= 5 ? 'bg-amber-50' : 'bg-emerald-50'
                      }`}>
                        <AlertTriangle className={`w-4 h-4 ${
                          marker.violationCount >= 10 ? 'text-rose-500' : 
                          marker.violationCount >= 5 ? 'text-amber-500' : 'text-emerald-500'
                        }`} />
                        <span className={`font-medium ${
                          marker.violationCount >= 10 ? 'text-rose-700' : 
                          marker.violationCount >= 5 ? 'text-amber-700' : 'text-emerald-700'
                        }`}>
                          {marker.violationCount} violation{marker.violationCount !== 1 ? 's' : ''}
                        </span>
                      </div>
                      
                      {Object.keys(marker.violationsByType).length > 0 && (
                        <div className="mt-2 space-y-1">
                          {Object.entries(marker.violationsByType).map(([type, count]) => (
                            count > 0 && (
                              <div key={type} className="flex justify-between text-xs">
                                <span className="text-gray-600">{DATASET_LABELS[type] || type}</span>
                                <span className="font-medium text-gray-900">{count}</span>
                              </div>
                            )
                          ))}
                        </div>
                      )}
                      
                      {marker.datasets?.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-gray-100">
                          <p className="text-xs text-gray-500 mb-1">Monitoring:</p>
                          <div className="flex flex-wrap gap-1">
                            {marker.datasets.map(ds => (
                              <span key={ds} className="text-xs bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded">
                                {DATASET_LABELS[ds] || ds}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          )}
        </div>

        {/* Stats */}
        {markers.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="glass-card rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-white">{markers.length}</p>
              <p className="text-sm text-zinc-400">Properties</p>
            </div>
            <div className="glass-card rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-white">
                {markers.reduce((sum, m) => sum + m.violationCount, 0)}
              </p>
              <p className="text-sm text-zinc-400">Total Violations</p>
            </div>
            <div className="glass-card rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-rose-400">
                {markers.filter(m => m.violationCount >= 10).length}
              </p>
              <p className="text-sm text-zinc-400">High Risk</p>
            </div>
            <div className="glass-card rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-emerald-400">
                {markers.filter(m => m.violationCount < 5).length}
              </p>
              <p className="text-sm text-zinc-400">Compliant</p>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
